import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import {
  BILLING,
  DELIVERIES_ADMIN,
  EMPLOYEE,
  GLOBAL_ADMIN,
  LOCATION_ADMIN,
  RECEPTIONIST,
  SECURITY,
} from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

const CAN_BULK_CHANGE_DELIVERY_AREA = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST, DELIVERIES_ADMIN];

const CAN_BULK_DELETE_DELIVERIES = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST, DELIVERIES_ADMIN];

const CAN_BULK_RESEND_NOTIFICATIONS = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST, DELIVERIES_ADMIN];

const CAN_EXPORT_DELIVERIES_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN];

const CAN_FILTER_BY_RECIPIENT_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST, DELIVERIES_ADMIN];

const CAN_VIEW_ALL_DELIVERIES_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST, DELIVERIES_ADMIN];

const CAN_VISIT_ROLES = [BILLING, EMPLOYEE, GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST, DELIVERIES_ADMIN, SECURITY];

export default class DeliveryLogAbility extends Ability {
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;

  get canBulkChangeDeliveryArea(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_AREA_UPDATE);
    }

    const { roleNames } = this.currentAdmin;
    const canChangeRoles = CAN_BULK_CHANGE_DELIVERY_AREA;

    return isPresent(_intersection(canChangeRoles, roleNames));
  }

  get canBulkDeleteDeliveries(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_DELETE);
    }

    const { roleNames } = this.currentAdmin;
    const canDeleteRoles = CAN_BULK_DELETE_DELIVERIES;

    return isPresent(_intersection(canDeleteRoles, roleNames));
  }

  get canBulkResendNotifications(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_UPDATE);
    }

    const { roleNames } = this.currentAdmin;
    const canResendRoles = CAN_BULK_RESEND_NOTIFICATIONS;

    return isPresent(_intersection(canResendRoles, roleNames));
  }

  get canExportDeliveries(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_EXPORT);
    }

    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_EXPORT_DELIVERIES_ROLES, roleNames));
  }

  get canFilterByRecipient(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_READ);
    }

    const { roleNames } = this.currentAdmin;
    const canFilterRoles = CAN_FILTER_BY_RECIPIENT_ROLES;

    return isPresent(_intersection(canFilterRoles, roleNames));
  }

  get canViewAllDeliveries(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return (
        this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_READ) ||
        this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_READ_MINE)
      );
    }

    const { roleNames } = this.currentAdmin;
    const canViewAllRoles = CAN_VIEW_ALL_DELIVERIES_ROLES;

    return isPresent(_intersection(canViewAllRoles, roleNames));
  }

  get canViewUnidentifiedDeliveries(): boolean {
    return this.canViewAllDeliveries;
  }

  get canVisit(): boolean {
    // Feature flag to enable Cerbos-based authorization
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return (
        this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_READ) ||
        this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_READ_MINE)
      );
    }

    // Legacy role-based authorization
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_VISIT_ROLES, roleNames));
  }
}
